import {ProductDataStore} from "../../stores/productDataStore";
import {ProductDataService} from "../../services/productDataService";
import {AppStateService} from "../../services/appStateService";
import {UIService} from "../UIService";

export class ConfiguratorHeaderService {

    private productDataService: ProductDataService;
    private productDataStore: ProductDataStore;
    private appStateService: AppStateService;
    private uiService: UIService;

    constructor() {
        this.productDataService = new ProductDataService();
        this.productDataStore = new ProductDataStore();
        this.appStateService = new AppStateService();
        this.uiService = new UIService();
    }

    calculateDimensionValue(value: number)
    {
        const currentUnit = this.getSelectedUnit();

        return Math.round((value - currentUnit.attributes.montageSpace) * currentUnit.attributes.multiplier);
    }

    calculateMillimetersFromDecimeters(dmValue)
    {
        const allUnits = this.productDataStore.getAvailableOptionsByName('unit');
        const decimeterUnit = allUnits.find(unit => { return unit.attributes.unit === 'dm' });

        return Math.round((dmValue - decimeterUnit.attributes.montageSpace) * decimeterUnit.attributes.multiplier);
    }

    getAvailableWidths()
    {
        let availableSizes = this.productDataStore.getAttributeByName('availableSizes');
        return Object.keys(availableSizes).map(el => parseInt(el)).sort((a, b) => (a - b));
    }

    getAvailableHeightsByWidth(selectedWidth)
    {
        let availableSizes = this.productDataStore.getAttributeByName('availableSizes');
        return availableSizes[selectedWidth];
    }

    getDmHeightBasedOnWidth(selectedWidth, currentHeight)
    {
        const availableSizes = this.getAvailableHeightsByWidth(selectedWidth);
        if (availableSizes.includes(currentHeight)) {
            return currentHeight;
        } else {
            return availableSizes[0];
        }
    }

    getWidthInDecimeterBasedOnMillimeters(millimeterWidth): number
    {
        const selectedUnit = this.productDataStore.getSelectedOptionData('unit')
        const availableSizes = this.productDataStore.getAttributeByName('availableSizes');

        const smallestWidthInDecimeter = parseInt(Object.keys(availableSizes)[0]);
        const decimeterWidth = Math.round(millimeterWidth / selectedUnit.attributes.multiplier);

        return decimeterWidth < smallestWidthInDecimeter ? smallestWidthInDecimeter : decimeterWidth;
    }

    getHeightInDecimeterBasedOnMillimeters(selectedDecimeterWidth, millimeterHeight)
    {
        const selectedUnit = this.productDataStore.getSelectedOptionData('unit')
        const decimeterHeight = Math.round(millimeterHeight / selectedUnit.attributes.multiplier);

        const availableSizes = this.productDataStore.getAttributeByName('availableSizes');
        const availableHeights = availableSizes[selectedDecimeterWidth];

        return this.getFirstMatchedHeight(decimeterHeight, availableHeights);
    }

    getBottomPanelInDecimeterBasedOnMillimeters(millimeterBottomPanel)
    {
        const selectedUnit = this.productDataStore.getSelectedOptionData('unit')
        return Math.round(millimeterBottomPanel / selectedUnit.attributes.multiplier);
    }

    validateSelectedWidth(mmWidthSelected: number): number
    {
        const minWidth = this.productDataStore.getAttributeByName('minWidth');
        const maxWidth = this.productDataService.getMaximumProductWidth();
        const maxWidthForColor = this.productDataService.getMaximumProductWidthForColor();

        if (mmWidthSelected < minWidth) {
            mmWidthSelected = minWidth;
            this.appStateService.setAppSettingTemplateFlag('configuratorHeader', 'widthError', true);
        }

        if (mmWidthSelected > maxWidth) {
            mmWidthSelected = maxWidth;
            this.appStateService.setAppSettingTemplateFlag('configuratorHeader', 'widthError', true);
        }

        if (mmWidthSelected > maxWidthForColor) {
            this.uiService.displayTopNotification('maximumDimensionsColorRuleNotice');
        }

        this.uiService.replaceTextInElement(this.uiService.getElementByClassName('configuratorHeader__widthMin'), minWidth.toString());
        this.uiService.replaceTextInElement(this.uiService.getElementByClassName('configuratorHeader__widthMax'), maxWidth.toString());

        return mmWidthSelected;
    }

    //@todo: bottom panel size
    validateSelectedHeight(mmHeightSelected: number, mmWidthSelected: number): number
    {
        const minHeight = this.getMinimumHeightByWidth(mmWidthSelected);
        const maxHeight = this.getMaximumHeightByWidth(mmWidthSelected);
        const maxHeightForColor = this.productDataService.getMaximumProductHeightForColor();

        if (mmHeightSelected < minHeight) {
            mmHeightSelected = minHeight;
            this.appStateService.setAppSettingTemplateFlag('configuratorHeader', 'heightError', true);
        }

        if (mmHeightSelected > maxHeight) {
            mmHeightSelected = maxHeight;
            this.appStateService.setAppSettingTemplateFlag('configuratorHeader', 'heightError', true);
        }

        if (mmHeightSelected > maxHeightForColor) {
            this.uiService.displayTopNotification('maximumDimensionsColorRuleNotice');
        }

        this.uiService.replaceTextInElement(this.uiService.getElementByClassName('configuratorHeader__heightMin'), minHeight.toString());
        this.uiService.replaceTextInElement(this.uiService.getElementByClassName('configuratorHeader__heightMax'), maxHeight.toString());

        return mmHeightSelected;
    }

    /*
     * In this case we just need to remember that bottom panel size means "glass size"
     * so it cannot be just higher than product height
     */
    validateSelectedBottomPanelHeight(bottomPanelHeight): number
    {
        const minimumValue = this.productDataStore.getAttributeByName('minBottomPanel');
        const maximumValue = this.productDataStore.getAttributeByName('height');
        let validatedBottomPanelHeight = parseInt(bottomPanelHeight);

        if (bottomPanelHeight < minimumValue) {
            this.appStateService.setAppSettingTemplateFlag('configuratorHeader', 'bottomPanelError', true);
            validatedBottomPanelHeight = minimumValue;
        }

        if (bottomPanelHeight > maximumValue) {
            this.appStateService.setAppSettingTemplateFlag('configuratorHeader', 'bottomPanelError', true);
            validatedBottomPanelHeight = maximumValue;
        }

        this.uiService.replaceTextInElement(this.uiService.getElementByClassName('configuratorHeader__bottomPanelMin'), minimumValue.toString());
        this.uiService.replaceTextInElement(this.uiService.getElementByClassName('configuratorHeader__bottomPanelMax'), maximumValue.toString());

        return validatedBottomPanelHeight;
    }

    getAvailableBottomPanelSizes(selectedDecimeterHeight, minimalGlassHeight)
    {
        const glassSizes = [];
        glassSizes.push('Helglas');
        if (minimalGlassHeight % 100 == 0 || minimalGlassHeight % 100 > 80) {
            minimalGlassHeight += 100;
        }
        for (let i = Math.ceil(minimalGlassHeight / 100); i < selectedDecimeterHeight; ++i) {
            glassSizes.push(i);
        }
        return glassSizes;
    }

    private getMinimumHeightByWidth(mmWidth): number
    {
        const millimeterSizes = this.productDataStore.getAttributeByName('availableSizesInMilimeters');
        const widthRanges = Object.keys(millimeterSizes);
        const widthRange = widthRanges.find(width => mmWidth <= width);
        return millimeterSizes[widthRange].minHeight;
    }

    private getMaximumHeightByWidth(mmWidth): number
    {
        const millimeterSizes = this.productDataStore.getAttributeByName('availableSizesInMilimeters');
        const widthRanges = Object.keys(millimeterSizes);
        const widthRange = widthRanges.find(width => mmWidth <= width);
        const maxHeight = millimeterSizes[widthRange].maxHeight;
        return maxHeight;
    }

    private getSelectedUnit() {
        const currentUnitId = this.productDataStore.getSelectedOptionIdByName('unit');
        return this.productDataStore.getAvailableOptionByNameAndId('unit', currentUnitId);
    }

    private getFirstMatchedHeight(decimeterHeight: number, availableHeights): number
    {
        // we use '+' in front of curr and prev just to be sure that js will cast value into number
        return availableHeights.reduce(function(prev, curr) {
            return (Math.abs(+curr - decimeterHeight) < Math.abs(+prev - decimeterHeight) ? +curr : +prev);
        });
    }
}
